import React, { useState } from "react";
import { X, Wand2, Loader2, RotateCw, History as HistoryIcon, ArrowLeftRight } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import LimitReachedModal from "../LimitReachedModal";

/**
 * AIEditPanel — text-prompt image editing (FLUX.1 Kontext [pro] via Fal.ai).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - currentSrc: data URL or http URL of the image being edited
 *  - history: [{ src, prompt, ts }] (most-recent-first)
 *  - onAccept: (newDataUrl, prompt) => void  -- caller swaps the canvas image
 *  - onRevert: (entry) => void               -- caller swaps to that history entry
 *  - coverProjectId: optional uuid for analytics
 */
const AIEditPanel = ({
  open,
  onClose,
  currentSrc,
  history = [],
  onAccept,
  onRevert,
  coverProjectId,
}) => {
  const { profile, refreshProfile } = useAuth();
  const [tab, setTab] = useState("edit");
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);

  if (!open) return null;

  const aiUsed = profile?.ai_images_used_this_month || 0;
  const aiLimit = profile?.plan === "pro" ? 30 : profile?.plan === "starter" ? 10 : 1;

  const apply = async () => {
    if (!prompt.trim() || !currentSrc) return;
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/cover/edit-image", {
        prompt: prompt.trim(),
        image_data_url: currentSrc,
        cover_project_id: coverProjectId || null,
      });
      setResult(data);
      refreshProfile();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "limit_reached") {
        setLimitInfo({ type: detail.type || "ai_image", plan: detail.plan || profile?.plan || "free" });
      } else {
        setError(typeof detail === "string" ? detail : "Edit failed. Try a more specific prompt.");
      }
    } finally {
      setLoading(false);
    }
  };

  const accept = () => {
    if (!result) return;
    onAccept?.(result.image_data_url || result.image_url, prompt.trim());
    setResult(null);
    setPrompt("");
  };

  const QUICK = [
    "Darken the sky and add a moody atmosphere",
    "Brighten the overall lighting",
    "Change to a winter scene with snow",
    "Make it more vibrant and saturated",
    "Apply a vintage film grain look",
    "Change the time of day to golden hour",
  ];

  return (
    <div
      className="fixed inset-y-0 right-0 z-40 w-[480px] bg-white border-l border-[#D3D1C7] shadow-2xl flex flex-col"
      data-testid="ai-edit-panel"
    >
      <div className="px-5 py-4 border-b border-[#D3D1C7] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-[#534AB7]" />
          <h3
            className="text-sm font-medium text-[#2C2C2A]"
            style={{ fontFamily: "Outfit, Inter, sans-serif" }}
          >
            Edit image with AI
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
          aria-label="Close panel"
          data-testid="ai-edit-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-5 pt-3">
        <div className="inline-flex bg-[#F1EFE8] rounded-[8px] p-1 gap-1" data-testid="ai-edit-tabs">
          <button
            onClick={() => setTab("edit")}
            data-testid="ai-edit-tab-edit"
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
              tab === "edit" ? "bg-white text-[#534AB7] shadow-sm" : "text-[#5F5E5A] hover:text-[#2C2C2A]"
            }`}
          >
            <Wand2 className="w-3 h-3" /> Edit
          </button>
          <button
            onClick={() => setTab("history")}
            data-testid="ai-edit-tab-history"
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 ${
              tab === "history" ? "bg-white text-[#534AB7] shadow-sm" : "text-[#5F5E5A] hover:text-[#2C2C2A]"
            }`}
          >
            <HistoryIcon className="w-3 h-3" /> History
            {history.length > 0 && (
              <span className="text-[10px] bg-[#534AB7] text-white rounded-full w-4 h-4 inline-flex items-center justify-center">
                {history.length}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {tab === "edit" && (
          <>
            <div className="rounded-[10px] overflow-hidden border border-[#D3D1C7] bg-[#F1EFE8]">
              {currentSrc ? (
                <img src={currentSrc} alt="Current" className="w-full max-h-44 object-contain bg-white" />
              ) : (
                <div className="aspect-[3/4] flex items-center justify-center text-xs text-[#5F5E5A]">
                  No image selected
                </div>
              )}
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] bg-white border-t border-[#D3D1C7]">
                Current image
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">
                Describe the change
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                data-testid="ai-edit-prompt-input"
                placeholder="e.g. Change the sky to a dramatic sunset, add stormy clouds, make the figure smaller"
                className="w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm focus:outline-none focus:border-[#534AB7] resize-none"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {QUICK.map((q) => (
                  <button
                    key={q}
                    onClick={() => setPrompt(q)}
                    type="button"
                    className="text-[11px] px-2 py-1 rounded-md border border-[#D3D1C7] text-[#5F5E5A] hover:bg-[#F1EFE8] hover:text-[#2C2C2A]"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-sm text-[#BA7517]" data-testid="ai-edit-error">{error}</p>
            )}

            <button
              onClick={apply}
              disabled={!prompt.trim() || loading || !currentSrc}
              data-testid="ai-edit-apply-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {loading ? "Editing your image…" : "Apply changes"}
            </button>

            <p className="text-xs text-[#5F5E5A] text-center" data-testid="ai-edit-usage-counter">
              {aiUsed} of {aiLimit} AI generations used this month · edits count the same as generates
            </p>

            {result && (
              <div className="border border-[#534AB7] rounded-[12px] overflow-hidden" data-testid="ai-edit-result">
                <div className="grid grid-cols-2 gap-px bg-[#D3D1C7]">
                  <div className="bg-white">
                    <img src={currentSrc} alt="Before" className="w-full" />
                    <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A]">Before</div>
                  </div>
                  <div className="bg-white">
                    <img src={result.image_data_url || result.image_url} alt="After" className="w-full" />
                    <div className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-[#534AB7]">After</div>
                  </div>
                </div>
                <div className="p-3 grid grid-cols-2 gap-2 bg-[#FAFAF9]">
                  <button
                    onClick={accept}
                    data-testid="ai-edit-accept-btn"
                    className="px-3 py-2 rounded-md bg-[#534AB7] text-white text-xs font-medium hover:bg-[#3C3489] flex items-center justify-center gap-1.5"
                  >
                    Use edited image
                  </button>
                  <button
                    onClick={apply}
                    disabled={loading}
                    data-testid="ai-edit-tryagain-btn"
                    className="px-3 py-2 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8] flex items-center justify-center gap-1.5"
                  >
                    <RotateCw className="w-3 h-3" /> Try again
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {tab === "history" && (
          <div className="space-y-3" data-testid="ai-edit-history-list">
            {history.length === 0 && (
              <p className="text-xs text-[#5F5E5A] italic text-center py-8">
                No edits yet in this session. Apply your first edit and the previous versions will appear here.
              </p>
            )}
            {history.map((h, i) => (
              <div
                key={`${h.ts}-${i}`}
                className="flex items-center gap-3 rounded-[10px] border border-[#D3D1C7] p-2 hover:border-[#534AB7] transition-colors"
                data-testid={`ai-edit-history-${i}`}
              >
                <img src={h.src} alt="" className="w-14 h-20 object-cover rounded-md bg-[#F1EFE8] border border-[#D3D1C7]" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#2C2C2A] line-clamp-2">{h.prompt || "Original"}</div>
                  <div className="text-[10px] text-[#5F5E5A] mt-0.5">
                    {new Date(h.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <button
                  onClick={() => { onRevert?.(h); onClose?.(); }}
                  data-testid={`ai-edit-revert-${i}`}
                  className="px-2.5 py-1.5 rounded-md text-[11px] border border-[#D3D1C7] hover:bg-[#534AB7] hover:text-white hover:border-[#534AB7] flex items-center gap-1"
                  title="Restore this version"
                >
                  <ArrowLeftRight className="w-3 h-3" /> Restore
                </button>
              </div>
            ))}
            {history.length > 0 && (
              <p className="text-[11px] text-[#5F5E5A] text-center italic pt-2">
                History is only kept for this editing session.
              </p>
            )}
          </div>
        )}
      </div>

      <LimitReachedModal
        open={!!limitInfo}
        type={limitInfo?.type || "ai_image"}
        plan={limitInfo?.plan || "free"}
        onClose={() => setLimitInfo(null)}
      />
    </div>
  );
};

export default AIEditPanel;
